import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ChevronDown, Building2, Users, Download, Search, FileImage, ArrowLeft } from "lucide-react";
import { api, formatApiError, API, authQuery } from "../lib/api";
import SuperAdminCategoryTabs from "../components/SuperAdminCategoryTabs";

export default function PassportsBrowserPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  void t;

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // {key: bool}
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/superadmin/passports/tree");
        setTree(data || []);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (key) => setExpanded((s) => ({ ...s, [key]: !s[key] }));

  const downloadPdf = async (memberId, lastName) => {
    try {
      const resp = await api.get(`/superadmin/passports/member/${memberId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `pasaport_${(lastName || "membru").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Filter the tree by name / passport / lodge / obedience.
  const filteredTree = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return tree;
    return tree
      .map((ob) => {
        const obMatch = (ob.name || "").toLowerCase().includes(q);
        const lodges = ob.lodges
          .map((l) => {
            const lodgeMatch = (l.name || "").toLowerCase().includes(q) || (l.city || "").toLowerCase().includes(q);
            const members = l.members.filter((m) => {
              const fullname = `${m.first_name || ""} ${m.last_name || ""}`.toLowerCase();
              return (
                fullname.includes(q) ||
                (m.passport_number || "").toLowerCase().includes(q) ||
                lodgeMatch ||
                obMatch
              );
            });
            return { ...l, members, _show: obMatch || lodgeMatch || members.length > 0 };
          })
          .filter((l) => l._show);
        return { ...ob, lodges, _show: obMatch || lodges.length > 0 };
      })
      .filter((ob) => ob._show);
  }, [tree, filter]);

  const photoUrl = (path) => path && `${API}/photo?path=${encodeURIComponent(path)}&${authQuery()}`;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10" data-testid="passports-browser-page">
      <div>
        <h1 className="page-title">{t("passportsBrowser.passports_folder_browser")}</h1>
        <p className="page-subtitle">
          {t("passportsBrowser.hierarchical_tree_of_every_issued")}
        </p>
      </div>
      <div className="gold-divider my-6" />

      <SuperAdminCategoryTabs active="passports" />

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <button className="btn-outline-gold flex items-center gap-1" onClick={() => navigate("/superadmin")}>
          <ArrowLeft size={12} /> Super-Admin
        </button>
        <div className="flex items-center gap-2 flex-1" style={{ minWidth: 260 }}>
          <Search size={14} className="text-gray-500" />
          <input
            className="input-lodge"
            placeholder={t("passportsBrowser.search_by_name_passport_lodge")}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="passports-tree-filter"
          />
        </div>
      </div>

      {loading ? (
        <div className="kv-label">{t("passportsBrowser.loading")}</div>
      ) : filteredTree.length === 0 ? (
        <div className="kv-label" style={{ fontStyle: "italic" }}>
          {t("passportsBrowser.no_passports_issued_yet")}
        </div>
      ) : (
        <div className="space-y-3" data-testid="passports-tree">
          {filteredTree.map((ob) => {
            const obKey = `ob:${ob.name}`;
            const obOpen = expanded[obKey] !== false; // open by default
            const totalMembers = ob.lodges.reduce((s, l) => s + (l.members?.length || 0), 0);
            return (
              <div key={obKey} className="panel">
                <button
                  type="button"
                  className="panel-head flex items-center justify-between w-full"
                  onClick={() => toggle(obKey)}
                  data-testid={`obedience-toggle-${ob.name}`}
                  style={{ cursor: "pointer", textAlign: "left" }}
                >
                  <span className="flex items-center gap-2">
                    {obOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <Building2 size={14} /> {ob.name}
                  </span>
                  <span className="kv-label" style={{ fontSize: "0.65rem" }}>
                    {ob.lodges.length} {t("passportsBrowser.lodges")} · {totalMembers} {t("passportsBrowser.members")}
                  </span>
                </button>
                {obOpen && (
                  <div className="panel-body space-y-3">
                    {ob.lodges.map((l) => {
                      const lodgeKey = `lodge:${l.id}`;
                      const lodgeOpen = expanded[lodgeKey] !== false;
                      return (
                        <div key={lodgeKey} className="upload-box" style={{ padding: "0.6rem 0.85rem" }}>
                          <button
                            type="button"
                            className="flex items-center justify-between w-full"
                            onClick={() => toggle(lodgeKey)}
                            data-testid={`lodge-toggle-${l.id}`}
                            style={{ background: "transparent", border: "none", cursor: "pointer" }}
                          >
                            <span className="flex items-center gap-2" style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.1em" }}>
                              {lodgeOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              <Users size={14} /> {l.name}
                              {l.city && <span className="kv-label" style={{ marginLeft: 6 }}>{l.city}, {l.country}</span>}
                            </span>
                            <span className="kv-label" style={{ fontSize: "0.65rem" }}>
                              {l.members.length} {t("passportsBrowser.members")}
                            </span>
                          </button>
                          {lodgeOpen && (
                            <div className="mt-3 space-y-1">
                              {l.members.length === 0 ? (
                                <div className="kv-label" style={{ fontStyle: "italic" }}>
                                  {t("passportsBrowser.no_members_with_passports_in")}
                                </div>
                              ) : (
                                l.members.map((m) => (
                                  <div
                                    key={m.id}
                                    className="flex items-center gap-3 py-2"
                                    style={{ borderTop: "1px solid var(--cream-200)" }}
                                    data-testid={`passport-row-${m.id}`}
                                  >
                                    {m.photo_url ? (
                                      <img
                                        src={photoUrl(m.photo_url)}
                                        alt=""
                                        style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: "1px solid #c9a86b" }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: "50%",
                                          background: "#e8d59a",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color: "#0d1b2a",
                                        }}
                                      >
                                        <FileImage size={14} />
                                      </div>
                                    )}
                                    <span style={{ flex: 1, fontFamily: "Crimson Text, serif", fontSize: "0.95rem" }}>
                                      <strong>
                                        {m.last_name} {m.first_name}
                                      </strong>{" "}
                                      <span className="kv-label" style={{ marginLeft: 6 }}>
                                        {m.passport_number || "—"} · {m.grade || ""}
                                      </span>
                                    </span>
                                    <span className="kv-label" style={{ fontSize: "0.65rem" }}>
                                      {t("passportsBrowser.init")}: {m.date_initiated || "—"}
                                    </span>
                                    <button
                                      className="btn-outline-gold flex items-center gap-1"
                                      onClick={() => downloadPdf(m.id, m.last_name)}
                                      data-testid={`download-passport-pdf-${m.id}`}
                                    >
                                      <Download size={12} /> PDF
                                    </button>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
